import React, {FC} from "react"

import {H1, H3, Label} from "@irati/ui/src/Typography"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {Col} from "@irati/ui/src/ColumnRow"
import {PaymentCurrencyType, UserRedemptionReportSavings} from "@irati/types"

import TitleIcon from "../TitleIcon"

interface Props {
  savings: UserRedemptionReportSavings
  previousSavings?: UserRedemptionReportSavings
  showIcon?: boolean
}

const Balance: FC<Props> = ({savings, previousSavings, showIcon = true}) => {
  const hasSavingsThisMonth = savings.total.value > 0
  const showSecondaryBalance = !hasSavingsThisMonth

  const balanceValues = showSecondaryBalance
    ? {value: previousSavings?.total?.value, currency: previousSavings?.total?.currency}
    : {value: savings.total.value, currency: savings.total.currency}

  const hasNoSavings = !balanceValues.value

  return (
    <Flex alignItems="center" gap={1.5} wrap={!hasSavingsThisMonth ? "wrap" : "nowrap"}>
      {showIcon && (
        <Col padding={0} xsHidden>
          <TitleIcon inverted />
        </Col>
      )}
      {hasNoSavings && (
        <H3>
          <FormattedMessage id="platform.mySavings.balance.noSavings.copy" />
        </H3>
      )}
      {!hasNoSavings && (
        <>
          {showSecondaryBalance && <SecondaryBalance currency={balanceValues.currency} />}
          <PrimaryBalance value={balanceValues.value} currency={balanceValues.currency} />
          {showSecondaryBalance && (
            <Label uppercase>
              <FormattedMessage id="platform.mySavings.balance.lastMonth.copy" />
            </Label>
          )}
        </>
      )}
    </Flex>
  )
}

interface BalanceProps {
  value?: number
  currency: PaymentCurrencyType
}

const PrimaryBalance: FC<BalanceProps> = ({value = 0, currency}) => {
  return (
    <H1 as="span" display="inline">
      <FormattedCurrency value={value} currency={currency} />
    </H1>
  )
}

const SecondaryBalance: FC<BalanceProps> = ({value = 0, currency}) => {
  const theme = useTheme()
  return (
    <H3 as="span" display="inline" color={theme.colors.greys.grey50}>
      <FormattedCurrency value={value} currency={currency} /> /
    </H3>
  )
}

export default Balance
